import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactElement } from 'react';
import { cn } from './utils';

/**
 * Der Hinweiskasten aus shadcn/ui. Er trägt `role="alert"` — dieselbe Rolle,
 * über die `e2e/vendors.spec.ts:109` die Fehlermeldung findet.
 *
 * **Die Bedeutungsfarbe kommt jetzt als Deckkraft und nicht als eigene Stufe.**
 * Bisher stand da `border-red-300 bg-red-50 text-red-800` mit einer
 * handgeschriebenen dunklen Entsprechung. Jetzt steht `--destructive` einmal
 * (im Hellen `red-700`, im Dunkeln `red-400`), und Fläche und Kante entstehen
 * daraus mit 10 % und 40 % Deckkraft. Eine Farbe, drei Verwendungen, und die
 * dunkle Fassung kann nicht mehr vergessen werden.
 *
 * NACHGERECHNET, weil die Zuordnung sich damit verschiebt (Grenze 4,5:1):
 *
 * | | Fläche | Text darauf |
 * |---|---|---|
 * | hell | `red-700` 10 % auf weiss = `#f8e8e8` | `red-700` → **5,45:1** |
 * | dunkel | `red-400` 10 % auf `slate-900` = `#262031` | `red-400` → **5,69:1** |
 *
 * Vorher waren es 7,60:1 und 11,16:1. Der Kasten ist also ruhiger geworden und
 * bleibt über der Grenze — was er nicht darf, ist unter sie zu rutschen, und
 * deshalb steht die Rechnung hier und nicht in einem Merkzettel.
 *
 * **Das Raster mit `grid-cols-[0_1fr]` ist mitgekommen**, weil es die
 * eigentliche Leistung der Vorlage ist: Ohne Sinnbild sind es 0 px erste
 * Spalte, mit Sinnbild wird sie 1 rem breit — und Titel wie Text stehen in
 * beiden Fällen bündig. `calc(var(--spacing)*4)` aus der Tailwind-4-Fassung ist
 * dabei zu `1rem` geworden; `--spacing` gibt es auf 3.4 nicht.
 */
const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border ' +
    'px-3 py-2 text-sm has-[>svg]:grid-cols-[1rem_1fr] has-[>svg]:gap-x-3 ' +
    '[&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground',
        destructive: 'border-destructive/40 bg-destructive/10 text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function Alert({
  className,
  variant,
  ...props
}: ComponentProps<'div'> & VariantProps<typeof alertVariants>): ReactElement {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

/*
 * `line-clamp-1` aus der Vorlage ist NICHT mitgekommen. Der Titel eines
 * Hinweises ist hier der Meldungstext des Dienstes — «Die USt-IdNr. konnte
 * beim Bundeszentralamt nicht geprüft werden» —, und eine abgeschnittene
 * Fehlermeldung ist schlimmer als eine zweizeilige.
 */

function AlertDescription({ className, ...props }: ComponentProps<'div'>): ReactElement {
  return (
    <div
      data-slot="alert-description"
      // Die Vorlage dämpft den Beschreibungstext auf `text-destructive/90`.
      // Hier nicht: 5,45:1 mal 0,9 Deckkraft sind rund 4,7:1, und damit läge
      // die Vorgangskennung — das Einzige, womit ein Anwender einen Vorfall
      // meldbar macht — knapp über der Grenze statt komfortabel darüber.
      className={cn('col-start-2 grid justify-items-start gap-1 text-sm', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
